import React, { useEffect, useRef, useState, useCallback } from "react";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import { View } from "react-native";

const AudioRecorder = ({
  onForcedStop = () => {},
  onRecordingCompleted = () => {},
  onRecordingStarted = () => {},
  showMicrophoneSelection = true,
  ...props
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const [recordedUrl, setRecordedUrl] = useState("");
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  
  // Microphone selection state
  const [showDeviceSelection, setShowDeviceSelection] = useState(false);
  const [availableAudioDevices, setAvailableAudioDevices] = useState({ inputs: [], outputs: [] });
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  // Get all audio devices
  const getAllAudioDevices = useCallback(async () => {
    try {
      // Request permission first to get device labels
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      // Stop the temporary stream
      stream.getTracks().forEach(track => track.stop());
      
      const inputs = devices.filter(d => d.kind === 'audioinput');
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      
      return { inputs, outputs };
    } catch (error) {
      console.error("Error enumerating audio devices:", error);
      return { inputs: [], outputs: [] };
    }
  }, []);

  // Load audio devices
  const loadAudioDevices = useCallback(async () => {
    setIsLoadingDevices(true);
    try {
      const devices = await getAllAudioDevices();
      setAvailableAudioDevices(devices);
      
      // Select first microphone by default if none selected
      if (devices.inputs.length > 0 && !selectedAudioInputDevice) {
        setSelectedAudioInputDevice(devices.inputs[0].deviceId);
      }
    } catch (error) {
      console.error("Error loading audio devices:", error);
    } finally {
      setIsLoadingDevices(false);
    }
  }, [getAllAudioDevices, selectedAudioInputDevice]);

  // Handle opening device selection dialog
  const openDeviceSelection = useCallback(async () => {
    await loadAudioDevices();
    setShowDeviceSelection(true);
  }, [loadAudioDevices]);

  // Handle starting recording with selected device
  const handleStartRecording = useCallback(() => {
    setShowDeviceSelection(false);
    startRecordingWithDevice(selectedAudioInputDevice);
  }, [selectedAudioInputDevice]);

  // Track the time elapsed every second
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
      setTimeElapsed(0);
    }
    return () => {
      clearInterval(interval);
      setTimeElapsed(0);
    };
  }, [isRecording]);

  useEffect(() => {
    if (timeElapsed > 600) {
      stopRecording();
      onForcedStop();
    }
  }, [timeElapsed]);

  const startRecordingWithDevice = async (deviceId) => {
    try {
      setIsRecording(true);
      onRecordingStarted();

      // Check for supported MIME type
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/ogg")
        ? "audio/ogg"
        : null;

      if (!mimeType) {
        throw new Error("No compatible audio format supported in this browser");
      }

      // Build audio constraints - use selected device if available
      const audioConstraints = deviceId 
        ? { deviceId: { exact: deviceId } }
        : true;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      mediaStream.current = stream;

      // Use the detected MIME type
      mediaRecorder.current = new MediaRecorder(stream, { mimeType });

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        // Use the same MIME type for the Blob
        const recordedBlob = new Blob(chunks.current, { type: mimeType });
        const url = URL.createObjectURL(recordedBlob);
        setRecordedUrl(url);
        setRecordedBlob(recordedBlob);
        chunks.current = [];
        setIsRecording(false);
        onRecordingCompleted();
      };

      mediaRecorder.current.start();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach((track) => track.stop());
        mediaStream.current = null;
      }
    }
  };

  const startRecording = async () => {
    if (showMicrophoneSelection) {
      // Show device selection dialog first
      await openDeviceSelection();
    } else {
      // Start immediately with default microphone
      await startRecordingWithDevice(null);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.stop();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };

  useEffect(() => {
    return () => {
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, []);

  useEffect(() => {
    if (recordedUrl != "" && recordedBlob != null) {
      onRecordingCompleted(recordedBlob, recordedUrl);
      setRecordedBlob(null);
      setRecordedUrl("");
    }
  }, [recordedUrl, recordedBlob, onRecordingCompleted]);

  return (
    <>
      <AppIcon
        onPress={isRecording ? stopRecording : startRecording}
        isClickable
        icon={
          isRecording
            ? AppAssets?.icons?.stopGeneration
            : AppAssets?.icons?.microphone
        }
        size={16}
        color={
          isRecording ? currentTheme?.theme?.red : currentTheme?.theme?.primary
        }
        {...props}
      />

      {/* Microphone Selection Dialog */}
      {showDeviceSelection && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={() => setShowDeviceSelection(false)}
        >
          <div
            style={{
              backgroundColor: currentTheme?.theme?.lightGrey,
              padding: "24px",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              animation: "slideIn 0.2s ease-out",
              border: `1px solid ${currentTheme?.theme?.border_color}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: "16px" }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 12,
                  gap: 10,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.microphone}
                  size={22}
                  color={currentTheme?.theme?.primary}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.medium_font_size,
                    fontWeight: "600",
                  }}
                  color={currentTheme?.theme?.font_color}
                >
                  Start Recording
                </AppText>
              </View>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  lineHeight: 18.5,
                }}
                color={currentTheme?.theme?.darkGrey}
              >
                Select your preferred microphone to start transcribing speech.
              </AppText>
            </div>

            {/* Microphone Selection */}
            <div style={{ marginBottom: "20px" }}>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "500",
                  marginBottom: 8,
                }}
                color={currentTheme?.theme?.font_color}
              >
                🎤 Microphone
              </AppText>
              {isLoadingDevices ? (
                <AppText
                  style={{ fontSize: currentTheme?.theme?.small_font_size }}
                  color={currentTheme?.theme?.darkGrey}
                >
                  Loading devices...
                </AppText>
              ) : availableAudioDevices.inputs.length === 0 ? (
                <AppText
                  style={{ fontSize: currentTheme?.theme?.small_font_size }}
                  color={currentTheme?.theme?.red}
                >
                  No microphones found. Please connect a microphone and try again.
                </AppText>
              ) : (
                <select
                  value={selectedAudioInputDevice || ""}
                  onChange={(e) => setSelectedAudioInputDevice(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: `1px solid ${currentTheme?.theme?.border_color}`,
                    backgroundColor:
                      currentTheme?.theme?.inputBackground ||
                      currentTheme?.theme?.white,
                    color: "#000000",
                    fontSize: currentTheme?.theme?.small_font_size,
                    fontFamily: "normalFont",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {availableAudioDevices.inputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId.slice(0, 8)}...`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowDeviceSelection(false)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: `1px solid ${currentTheme?.theme?.border_color}`,
                  backgroundColor: "transparent",
                  color: currentTheme?.theme?.darkGrey,
                  fontSize: currentTheme?.theme?.small_font_size,
                  cursor: "pointer",
                  fontFamily: "normalFont",
                  transition: "all 0.2s",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleStartRecording}
                disabled={
                  availableAudioDevices.inputs.length === 0 || isLoadingDevices
                }
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor:
                    availableAudioDevices.inputs.length === 0 || isLoadingDevices
                      ? currentTheme?.theme?.darkGrey
                      : currentTheme?.theme?.primary,
                  color: "#fff",
                  fontSize: currentTheme?.theme?.small_font_size,
                  fontWeight: "600",
                  cursor:
                    availableAudioDevices.inputs.length === 0 || isLoadingDevices
                      ? "not-allowed"
                      : "pointer",
                  fontFamily: "normalFont",
                  transition: "all 0.2s",
                  opacity:
                    availableAudioDevices.inputs.length === 0 || isLoadingDevices
                      ? 0.6
                      : 1,
                }}
              >
                Start Recording
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AudioRecorder;
